import {useCallback, useState} from 'react'
import {
    useAccessToken,
    useConfig,
    useCustomerType,
    useEncUserId,
    useUsid
} from '@salesforce/commerce-sdk-react'
import {helpers as commerceHelpers} from 'commerce-sdk-isomorphic'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useBrand} from '../contexts/brand-context'
import {getSiteEnvVarValue} from '../utils/site-config'

const EINSTEIN_API_NAME = 'einstein'
const EINSTEIN_API_VERSION = 'v1'

/**
 * Checks whether the client-side Einstein integration is enabled in runtime config.
 *
 * @param {boolean|string} value - Runtime config value for `EINSTEIN_ENABLED`.
 * @returns {boolean} `true` only when the value is boolean `true` or string `'true'`.
 */
export const isEinsteinEnabled = (value) => value === true || value === 'true'

/**
 * Resolves the product identifier expected by the custom Einstein endpoints.
 *
 * @param {object|string} product - Product object from SCAPI/PDP state, or a product ID string.
 * @returns {?string} Product ID, variant product ID, master ID, or `null` when unavailable.
 */
export const getProductId = (product) => {
    if (!product) return null
    if (typeof product === 'string') return product

    return product.productId || product.id || product.master?.masterId || null
}

/**
 * Normalizes supported custom API response shapes into a product array.
 *
 * @param {object|Array} response - Response from `/get-recently-viewed`.
 * @returns {Array} Product records, or an empty array when the response has no product list.
 */
export const getRecentlyViewedProductsFromResponse = (response) => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.data?.data)) return response.data.data
    return []
}

const buildQueryParameters = (site, locale) => ({
    siteId: site?.id,
    ...(locale?.id ? {locale: locale.id} : {})
})

const buildClientConfig = (config) => ({
    parameters: {
        clientId: config.clientId,
        siteId: config.siteId,
        organizationId: config.organizationId,
        shortCode: config.shortCode
    },
    proxy: config.proxy,
    throwOnBadResponse: true
})

/**
 * Creates a POST caller for the SFCC custom Einstein API namespace.
 *
 * The returned function uses the current Commerce SDK config and access token,
 * then calls `/custom/einstein/v1/...` through `commerce-sdk-isomorphic`.
 *
 * @returns {Function} Async function accepting `{endpointPath, parameters, body}`.
 */
const useEinsteinCustomApi = () => {
    const config = useConfig()
    const {getTokenWhenReady} = useAccessToken()

    return useCallback(
        async ({endpointPath, parameters, body}) => {
            const token = await getTokenWhenReady()
            const headers = {
                'Content-Type': 'application/json',
                ...(config.headers || {}),
                Authorization: `Bearer ${token}`
            }

            return commerceHelpers.callCustomEndpoint({
                options: {
                    method: 'POST',
                    headers,
                    parameters,
                    body: JSON.stringify(body),
                    customApiPathParameters: {
                        apiName: EINSTEIN_API_NAME,
                        apiVersion: EINSTEIN_API_VERSION,
                        endpointPath
                    }
                },
                clientConfig: buildClientConfig(config),
                rawResponse: false
            })
        },
        [config, getTokenWhenReady]
    )
}

/**
 * Builds user identity fields required by the server-side Einstein custom APIs.
 *
 * @returns {Function} Async function resolving `{cookieId, userId?}`. `userId`
 * is included only for registered shoppers.
 */
const useEinsteinUserParameters = () => {
    const {getUsidWhenReady} = useUsid()
    const {getEncUserIdWhenReady} = useEncUserId()
    const {isRegistered} = useCustomerType()

    return useCallback(async () => {
        const cookieId = await getUsidWhenReady()
        const userId = isRegistered ? await getEncUserIdWhenReady() : undefined

        return {
            cookieId,
            ...(userId ? {userId} : {})
        }
    }, [getEncUserIdWhenReady, getUsidWhenReady, isRegistered])
}

/**
 * Reads feature guards for custom Einstein requests.
 *
 * @returns {{isEnabled: boolean}} Request guard state.
 */
const useEinsteinRequestGuards = () => {
    const {app: appConfig = {}} = getConfig()
    const {site} = useMultiSite()

    return {
        isEnabled: isEinsteinEnabled(getSiteEnvVarValue(appConfig, 'EINSTEIN_ENABLED', site?.id))
    }
}

/**
 * Hook for sending PDP product-view activity to the custom `/view-product` API.
 *
 * Requests are skipped when `EINSTEIN_ENABLED` is not true, when no site ID is
 * available, when no product ID can be resolved, or when `shouldFetch` is false.
 *
 * @returns {{
 *   sendViewProduct: Function,
 *   isLoading: boolean,
 *   error: ?Error,
 *   isEnabled: boolean
 * }} State plus `sendViewProduct(product, options)` action.
 */
export const useEinsteinViewProduct = () => {
    const {site, locale} = useMultiSite()
    const callEinsteinCustomApi = useEinsteinCustomApi()
    const getUserParameters = useEinsteinUserParameters()
    const {isEnabled} = useEinsteinRequestGuards()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    const sendViewProduct = useCallback(
        async (product, {shouldFetch = true} = {}) => {
            const productId = getProductId(product)

            if (!shouldFetch || !isEnabled || !site?.id || !productId) {
                return {success: false}
            }

            setIsLoading(true)
            setError(null)

            try {
                const userParameters = await getUserParameters()

                if (!userParameters.cookieId) {
                    return {success: false}
                }

                return await callEinsteinCustomApi({
                    endpointPath: '/view-product',
                    parameters: buildQueryParameters(site, locale),
                    body: {
                        ...userParameters,
                        productId
                    }
                })
            } catch (err) {
                setError(err)
                logger.error('Einstein view-product request failed', {
                    namespace: 'useEinsteinViewProduct.sendViewProduct',
                    additionalProperties: {error: err.message, stack: err.stack, productId}
                })

                return {success: false}
            } finally {
                setIsLoading(false)
            }
        },
        [callEinsteinCustomApi, getUserParameters, isEnabled, locale, site]
    )

    return {
        sendViewProduct,
        isLoading,
        error,
        isEnabled
    }
}

/**
 * Hook for fetching recently viewed products from the custom `/get-recently-viewed` API.
 *
 * Requests are skipped when `EINSTEIN_ENABLED` is not true, when no site ID or
 * brand is available, or when `shouldFetch` is false. The returned `products`
 * state is normalized from the custom API response shape.
 *
 * @returns {{
 *   getRecentlyViewed: Function,
 *   products: Array,
 *   isLoading: boolean,
 *   error: ?Error,
 *   isEnabled: boolean
 * }} State plus `getRecentlyViewed(options)` action.
 */
export const useEinsteinRecentlyViewed = () => {
    const {site, locale} = useMultiSite()
    const {brandId} = useBrand()
    const callEinsteinCustomApi = useEinsteinCustomApi()
    const getUserParameters = useEinsteinUserParameters()
    const {isEnabled} = useEinsteinRequestGuards()
    const [products, setProducts] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    const getRecentlyViewed = useCallback(
        async ({brand = brandId, shouldFetch = true} = {}) => {
            if (!shouldFetch || !isEnabled || !site?.id || !brand) {
                setProducts([])
                return []
            }

            setIsLoading(true)
            setError(null)

            try {
                const userParameters = await getUserParameters()

                if (!userParameters.cookieId) {
                    setProducts([])
                    return []
                }

                const response = await callEinsteinCustomApi({
                    endpointPath: '/get-recently-viewed',
                    parameters: buildQueryParameters(site, locale),
                    body: {
                        ...userParameters,
                        brand
                    }
                })
                const nextProducts = getRecentlyViewedProductsFromResponse(response)
                setProducts(nextProducts)

                return nextProducts
            } catch (err) {
                setError(err)
                setProducts([])
                logger.error('Einstein get-recently-viewed request failed', {
                    namespace: 'useEinsteinRecentlyViewed.getRecentlyViewed',
                    additionalProperties: {error: err.message, stack: err.stack, brand}
                })

                return []
            } finally {
                setIsLoading(false)
            }
        },
        [brandId, callEinsteinCustomApi, getUserParameters, isEnabled, locale, site]
    )

    return {
        getRecentlyViewed,
        products,
        isLoading,
        error,
        isEnabled
    }
}
